// Feature #320 — the assessment history block, as a change feed.
//
// What it replaced: every rating from every submission, printed flat. On a
// real profile that was 70 name/number pairs across five submissions to
// convey seven rating movements — roughly 90% of the ink was repetition.
//
// The design rule that shapes every row below: **only two things ever change
// a rating.** R1/R6 (the first rating on a skill) and R5 (a review agreeing
// a value). Under R3 an assessment whose rating differs from the current one
// opens a delta and leaves the rating alone — and `submit_assessment` has no
// branch on assessment type, so that is just as true of a *self* assessment
// as a peer one.
//
// So the arrow glyph is reserved for those two cases. A divergence renders
// as a `current / proposed` pair with no arrow, because rendering it as
// "5 → 3" would tell the person their rating moved when it did not — the
// exact confusion this feature exists to remove.
import React, { useMemo, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import {
  SmDelta,
  SmHistoryEvent,
  SmHistoryItem,
  SmHistoryReview,
  SmHistoryReviewOutcome,
  SmHistorySubmission,
} from '@/api/sm'
import {
  DirectionChip,
  EventChip,
  HistoryRecordTable,
  LabelledLevel,
  LevelToken,
  StateChip,
  blockStyle,
  toggleButton,
  toggleGroup,
} from './SmShared'

interface AssessmentFeedProps {
  /** The merged chronology, newest first. */
  history: SmHistoryEvent[]
  /** Both streams unabridged, for the Full record view. */
  submissions: SmHistorySubmission[]
  reviews: SmHistoryReview[]
  /**
   * #364 — the caller is reading their OWN history as an ordinary user.
   * The API has already withheld peer assessments about them; this only
   * changes the copy so the absence reads as a rule, not as "nobody has
   * assessed you".
   */
  subjectView?: boolean
}

type EventFilter = 'all' | 'self' | 'peer' | 'review'
type ViewMode = 'feed' | 'record'

const FILTERS: { key: EventFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'self', label: 'Self' },
  { key: 'peer', label: 'Peer' },
  { key: 'review', label: 'Reviews' },
]

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
}

const skillCell: React.CSSProperties = {
  minWidth: '235px',
  flex: '1 1 235px',
  fontSize: '14px',
}

const muted: React.CSSProperties = {
  fontSize: '12.5px',
  color: 'var(--color-dark-grey)',
}

/** Timeline marker. Shape carries the event type; colour only supports it. */
const EventDot: React.FC<{ kind: 'self' | 'peer' | 'review' }> = ({ kind }) => {
  const base: React.CSSProperties = {
    position: 'absolute',
    left: '-27px',
    top: '3px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    boxShadow: '0 0 0 3px var(--color-white)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: 700,
    color: 'var(--color-white)',
  }
  if (kind === 'review') {
    return (
      <span style={{ ...base, backgroundColor: 'var(--color-black)' }} aria-hidden="true">
        ✓
      </span>
    )
  }
  if (kind === 'peer') {
    return (
      <span
        style={{
          ...base,
          backgroundColor: 'var(--color-white)',
          border: '2.5px solid #475569',
        }}
        aria-hidden="true"
      />
    )
  }
  return (
    <span style={{ ...base, backgroundColor: 'var(--sm-level-3)' }} aria-hidden="true" />
  )
}

const Collapsible: React.FC<{ summary: string; children: React.ReactNode }> = ({
  summary,
  children,
}) => (
  <details>
    <summary
      style={{
        cursor: 'pointer',
        color: 'var(--color-dark-grey)',
        fontSize: '13px',
        padding: '5px 0',
      }}
    >
      {summary}
    </summary>
    <div style={{ display: 'grid', gap: '8px', padding: '6px 0 2px' }}>{children}</div>
  </details>
)

/** One submission — self or peer. The grammar is identical for both. */
const SubmissionEvent: React.FC<{ submission: SmHistorySubmission }> = ({ submission }) => {
  const opened = submission.items.filter((i) => i.outcome === 'delta_opened')
  const first = submission.items.filter((i) => i.outcome === 'first_rated')
  const confirmed = submission.items.filter((i) => i.outcome === 'confirmed')

  // How many earlier proposals this submission stacked onto, per skill (R4).
  // Counted within the submission's own view of history: the item carries the
  // baseline it was measured against, so a repeat proposal shows the same
  // `previous_rating` as the one before it.
  const summary = [
    opened.length > 0
      ? `${opened.length} delta${opened.length === 1 ? '' : 's'} opened`
      : null,
    first.length > 0 ? `${first.length} first rated` : null,
    confirmed.length > 0 ? `${confirmed.length} confirmed` : null,
    `${submission.items.length} skill${submission.items.length === 1 ? '' : 's'} rated`,
  ]
    .filter(Boolean)
    .join(' · ')

  const itemRow = (item: SmHistoryItem) => {
    if (item.outcome === 'first_rated') {
      return (
        <div key={item.skill_id} style={rowStyle}>
          <span style={skillCell}>{item.skill_name}</span>
          <LevelToken level={null} />
          <span style={{ color: 'var(--color-dark-grey)' }}>→</span>
          <LevelToken level={item.rating} />
          <StateChip>✚ first rated</StateChip>
          <span style={muted}>applies immediately</span>
        </div>
      )
    }
    return (
      <div key={item.skill_id} style={rowStyle}>
        <span style={skillCell}>{item.skill_name}</span>
        <LabelledLevel caption="current" level={item.previous_rating ?? null} />
        <LabelledLevel caption="proposed" level={item.rating} />
        <StateChip tone="open">◆ delta opened</StateChip>
        <span style={muted}>rating unchanged until reviewed</span>
      </div>
    )
  }

  return (
    <>
      <p style={{ ...muted, fontSize: '13px', margin: '4px 0 12px' }}>{summary}</p>
      <div style={{ display: 'grid', gap: '8px' }}>
        {opened.map(itemRow)}

        {/* A first submission is all-new, so it must collapse or the feed
            opens on a 20-row wall. Two or fewer stay inline — a single new
            skill reads better as a row than as an expander. */}
        {first.length > 0 && first.length <= 2 && first.map(itemRow)}
        {first.length > 2 && (
          <Collapsible
            summary={`✚ ${first.length} skills first rated — applied immediately`}
          >
            {first.map(itemRow)}
          </Collapsible>
        )}

        {confirmed.length > 0 && (
          <Collapsible
            summary={`✓ ${confirmed.length} skill${
              confirmed.length === 1 ? '' : 's'
            } confirmed at the current level`}
          >
            {confirmed.map((item) => (
              <div key={item.skill_id} style={rowStyle}>
                <span style={skillCell}>{item.skill_name}</span>
                <LevelToken level={item.rating} />
                <StateChip tone="muted">✓ agrees with current</StateChip>
              </div>
            ))}
          </Collapsible>
        )}

        {submission.items.length === 0 && (
          <span style={muted}>This submission recorded no ratings.</span>
        )}
      </div>
    </>
  )
}

/**
 * A review — the only event that resolves a delta, and the only place three
 * values exist at once. One row per skill because R4 lets several competing
 * proposals ride on the same rating.
 *
 * #364 — the rows come from `outcomes` (one per skill the review touched,
 * whoever proposed on it) and the attributed proposal chips from `deltas`
 * (only the ones the caller may see). For the subject that means a skill
 * can read "was 3 → agreed 4" with no proposal chip at all: somebody else
 * raised it, and their proposal is not the subject's to read. The agreed
 * number still is — it is their rating now.
 */
const ReviewEvent: React.FC<{ review: SmHistoryReview }> = ({ review }) => {
  const { deltas, status, meeting_notes: meetingNotes } = review
  const rows = useMemo(() => {
    const proposals = new Map<string, SmDelta[]>()
    deltas.forEach((d) => {
      const bucket = proposals.get(d.skill_id)
      if (bucket) bucket.push(d)
      else proposals.set(d.skill_id, [d])
    })
    const outcomes: SmHistoryReviewOutcome[] =
      review.outcomes && review.outcomes.length > 0
        ? review.outcomes
        : // A payload without outcomes: one per skill, off the deltas.
          Array.from(proposals.entries()).map(([skill_id, group]) => ({
            skill_id,
            skill_name: group[0].skill_name,
            previous_rating: group[0].previous_rating ?? null,
            agreed_rating: group.find((d) => d.agreed_rating != null)?.agreed_rating ?? null,
            resolved: group.every((d) => d.state === 'resolved'),
          }))
    return outcomes
      .map((outcome) => ({ outcome, proposals: proposals.get(outcome.skill_id) || [] }))
      .sort((a, b) =>
        (a.outcome.skill_name || a.outcome.skill_id).localeCompare(
          b.outcome.skill_name || b.outcome.skill_id
        )
      )
  }, [deltas, review.outcomes])

  const agreedCount = rows.filter((r) => r.outcome.agreed_rating != null).length
  const summary =
    status === 'resolved'
      ? [
          `${agreedCount} rating${agreedCount === 1 ? '' : 's'} changed`,
          rows.length - agreedCount > 0 ? `${rows.length - agreedCount} returned to open` : null,
          'agreed values take provenance “Review”',
        ]
          .filter(Boolean)
          .join(' · ')
      : `${rows.length} skill${rows.length === 1 ? '' : 's'} in review · nothing changes until each is agreed`

  return (
    <>
      <p style={{ ...muted, fontSize: '13px', margin: '4px 0 12px' }}>{summary}</p>
      <div style={{ display: 'grid', gap: '8px' }}>
        {rows.map(({ outcome, proposals }) => {
          const was = outcome.previous_rating ?? null
          const agreed = outcome.agreed_rating ?? null
          return (
            <div key={outcome.skill_id} style={rowStyle}>
              <span style={skillCell}>{outcome.skill_name || outcome.skill_id}</span>
              <LabelledLevel caption="was" level={was} />
              {/* #349 — with three or four proposals on one skill a hover
                  title is not attribution. The proposer is printed next to
                  each level, and the reason (#351) rides on the tooltip. */}
              {proposals.map((d) => {
                const proposer = d.proposed_by_display_name || d.proposed_by_user_id
                return (
                  <span
                    key={d.id}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                  >
                    <LabelledLevel
                      caption="proposed"
                      level={d.proposed_rating}
                      title={
                        d.comment
                          ? `Proposed by ${proposer}: ${d.comment}`
                          : `Proposed by ${proposer}`
                      }
                    />
                    <span style={{ ...muted, fontSize: '12px' }}>by {proposer}</span>
                  </span>
                )
              })}
              {agreed != null ? (
                <>
                  <span style={{ color: 'var(--color-dark-grey)' }}>→</span>
                  <LabelledLevel caption="agreed" level={agreed} emphasis />
                  {was != null && <DirectionChip from={was} to={agreed} />}
                  {/* #372 — the note recorded against this agreed rating. */}
                  {outcome.notes && (
                    <span style={{ ...muted, fontSize: '12px', flexBasis: '100%' }}>
                      Note: {outcome.notes}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <StateChip tone="muted">◇ returned to open</StateChip>
                  <span style={muted}>
                    {status === 'resolved'
                      ? 'the review closed before this was agreed'
                      : 'still to be agreed'}
                  </span>
                </>
              )}
            </div>
          )
        })}
        {rows.length === 0 && <span style={muted}>No deltas attached to this review.</span>}
      </div>
      {meetingNotes && (
        <p style={{ marginTop: '10px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
          Meeting notes: {meetingNotes}
        </p>
      )}
    </>
  )
}

export const AssessmentFeed: React.FC<AssessmentFeedProps> = ({
  history,
  submissions,
  reviews,
  subjectView = false,
}) => {
  const [view, setView] = useState<ViewMode>('feed')
  const [filter, setFilter] = useState<EventFilter>('all')

  const visible = useMemo(
    () => (filter === 'all' ? history : history.filter((e) => e.event_type === filter)),
    [history, filter]
  )
  // #364 — a Peer filter over a feed that can never contain a peer event
  // invites "No events of this type", which reads as "nobody has assessed
  // you". Offer the filter only when there is something for it to show.
  const filters = FILTERS.filter(
    (f) => f.key !== 'peer' || history.some((e) => e.event_type === 'peer')
  )

  return (
    <section style={blockStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Assessment history</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {view === 'feed' && (
            <div style={toggleGroup} role="group" aria-label="Filter history events">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  style={toggleButton(filter === f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <div style={toggleGroup} role="group" aria-label="History view">
            <button
              type="button"
              aria-pressed={view === 'feed'}
              onClick={() => setView('feed')}
              style={toggleButton(view === 'feed')}
            >
              Changes
            </button>
            <button
              type="button"
              aria-pressed={view === 'record'}
              onClick={() => setView('record')}
              style={toggleButton(view === 'record')}
            >
              Full record
            </button>
          </div>
        </div>
      </div>
      <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: '4px 0 18px' }}>
        An assessment only changes a rating the first time a skill is rated. After that a
        differing rating is a proposal, agreed at a review.
        {subjectView &&
          ' Assessments others make about you are not shown here; the ratings agreed at your reviews are.'}
      </p>

      {view === 'record' ? (
        <HistoryRecordTable submissions={submissions} reviews={reviews} />
      ) : visible.length === 0 ? (
        <p style={{ color: 'var(--color-dark-grey)' }}>
          {history.length === 0 ? 'No history yet.' : 'No events of this type.'}
        </p>
      ) : (
        <div
          style={{
            position: 'relative',
            paddingLeft: '30px',
          }}
        >
          {/* The rail. Absolutely positioned rather than a border on the list
              so it stops at the last dot instead of running past it. */}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '8px',
              top: '8px',
              bottom: '14px',
              width: '2px',
              backgroundColor: 'var(--color-light-grey)',
            }}
          />
          {visible.map((event) => {
            const key =
              event.submission?.submission_id || event.review?.review_id || event.occurred_at
            const actor =
              event.event_type === 'review'
                ? // #372 — a review names who conducted it, as an assessment
                  // names its assessor.
                  event.review?.conducted_by_display_name ||
                  event.review?.conducted_by_user_id
                  ? `Review by ${
                      event.review.conducted_by_display_name ||
                      event.review.conducted_by_user_id
                    }`
                  : 'Review'
                : `${event.event_type === 'peer' ? 'Peer' : 'Self'} assessment by ${
                    event.submission?.assessor_display_name ||
                    event.submission?.assessor_user_id ||
                    'unknown'
                  }`
            return (
              <div key={key} style={{ position: 'relative', paddingBottom: '24px' }}>
                <EventDot kind={event.event_type} />
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <span style={{ fontWeight: 650, fontSize: '14.5px' }}>
                    {formatDateTime(event.occurred_at)}
                  </span>
                  <EventChip kind={event.event_type}>{actor}</EventChip>
                </div>
                {event.submission && <SubmissionEvent submission={event.submission} />}
                {event.review && <ReviewEvent review={event.review} />}
              </div>
            )
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '18px',
          alignItems: 'center',
          marginTop: '18px',
          paddingTop: '14px',
          borderTop: '1px dashed var(--color-light-grey)',
          fontSize: '12.5px',
          color: 'var(--color-dark-grey)',
        }}
      >
        <span>
          <span style={{ color: 'var(--color-dark-grey)' }}>→</span> the rating changed
        </span>
        <span>◆ a proposal awaiting review</span>
        <span>✓ confirmed at the current level</span>
      </div>
    </section>
  )
}

export default AssessmentFeed
